<script setup lang="ts">
import UiIcon from 'ui-toolkit/components/UiIcon.vue';
import { IconSPin } from 'ui-toolkit/icons';

import ContentContainer from '@/components/layout/ContentContainer.vue';
import ContentColumn from '@/components/layout/ContentColumn.vue';
import type { FeaturedGridCells } from '@/types';
import type { GridCell } from '@/models/grid';

defineProps<{
  gridCells: FeaturedGridCells[];
}>();

function gridCellsLink(gridCellNames: GridCell['gridCellName'][]) {
  const mapRef = (gridCellNames.length > 1)
    ? `cells/${gridCellNames.sort().join(',')}`
    : `cell/${gridCellNames[0]}`;
  return `/map/${mapRef}?style=satellite`;
}
</script>

<template>
  <ContentContainer class="grid-cells-container">
    <ContentColumn>
      <div class="grid-cells-wrap">
        <NuxtLink
          v-for="cell in gridCells"
          :key="cell.gridCellNames.sort().join('|')"
          class="grid-cell"
          :to="gridCellsLink(cell.gridCellNames)"
        >
          <div class="grid-cell-thumb">
            <img class="grid-cell-image" :src="cell.image">
            <div class="grid-cell-thumb-scrim subhead-primary">
              View in Map
            </div>
          </div>
          <div class="grid-cell-label body-small">
            <UiIcon size="m">
              <IconSPin />
            </UiIcon>
            {{ cell.label }}
          </div>
        </NuxtLink>
      </div>
    </ContentColumn>
  </ContentContainer>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/shared/screen" as screen;

.grid-cells-container {
  :deep(.column-main) {
    @include screen.grid(12, 8, 4);
  }
}

.grid-cells-wrap {
  max-width: 120rem;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-l) var(--spacing-m);

}

.grid-cell {
  display: block;
  width: 21rem;
  text-decoration: none;

  @include screen.respond(mobile) {
    width: calc(50% - var(--spacing-m));
  }
}
.grid-cell-thumb {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--border-radius-m);
}
.grid-cell-image {
  display: block;
  width: 100%;
}
.grid-cell-thumb-scrim {
  position: absolute;
  top: 0; bottom: 0; left: 0; right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  text-decoration: underline;
  opacity: 0;
  transition: opacity var(--anim-speed) ease;

  .grid-cell:hover & {
    opacity: 1;
  }
}
.grid-cell-label {
  margin-top: var(--spacing-xs);
  display: flex;
  align-items: center;
  font-size: 1.15rem;
  font-weight: 500;
  color: var(--colour-dark-grey);
}
</style>
